/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import Dialog from '@material-ui/core/Dialog';
import {makeStyles} from '@material-ui/core/styles';
import {useWorkspaceRect} from '../../../hooks/useWorkspaceRect';
import {SystemSaveExportIcon} from '../../../icons/SystemSaveExportIcon';
import {dlsColor} from '../../../theme/theme';
import {IconSize} from '../../../types/types';
import {CloseButton} from '../../buttons/CloseButton';
import {DataTable, DataTableColumn, DataTableRow} from '../../table/current/DataTable';
import {SeverityTag} from '../../tags/current/SeverityTag';
import {getAssetDetailsContentStyles} from './assetDetailsContentStyles';

export interface FaultCodesModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  // the table's own title, e.g. `OBDII DTC View` ...
  viewTitle: string;
  // the active fault count tag, e.g. `18 Active Faults` ...
  countLabel: string;
  exportLabel: string;
  closeLabel: string;
  onExport: () => void;
  columns: DataTableColumn[];
  rows: DataTableRow[];
  // the workspace element the modal and its scrim cover - everything but the
  // left navigation (`layout-root`) by default ...
  workspaceId?: string;
}

// the Figma maximum (890px) is the 1024px-tall frame less 134px ...
const MODAL_MAX_HEIGHT = 'min(890px, calc(100vh - 134px))';
const TITLE_ID         = 'fault-codes-modal-title';

const useStyles = makeStyles(() => {
  const {title: contentTitle} = getAssetDetailsContentStyles();
  return {
    paper : {
      boxSizing     : 'border-box',
      display       : 'flex',
      flexDirection : 'column',
      width         : '880px',
      maxWidth      : 'calc(100% - 48px)',
      maxHeight     : MODAL_MAX_HEIGHT,
      margin        : 0,
      overflow      : 'hidden',
      borderRadius  : '12px'
    },
    header : {
      position       : 'relative',
      display        : 'flex',
      flexShrink     : 0,
      alignItems     : 'center',
      justifyContent : 'center',
      height         : '56px',
      borderBottom   : `1px solid ${dlsColor('grey5')}`
    },
    title : {
      margin     : 0,
      fontFamily : 'Inter',
      fontWeight : 600,
      fontSize   : '16px',
      lineHeight : '20px',
      color      : dlsColor('grey1')
    },
    close : {
      position  : 'absolute',
      top       : '50%',
      right     : '8px',
      transform : 'translateY(-50%)'
    },
    // the toolbar stays put and the table takes what's left, so at maximum
    // height only the table's body scrolls ...
    body : {
      boxSizing     : 'border-box',
      display       : 'flex',
      flex          : '1 1 auto',
      flexDirection : 'column',
      minHeight     : 0,
      padding       : '24px',
      gap           : '24px'
    },
    toolbar : {
      display        : 'flex',
      flexShrink     : 0,
      alignItems     : 'center',
      justifyContent : 'space-between',
      height         : '40px',
      gap            : '16px'
    },
    viewTitle : {
      ...contentTitle,
      margin : 0
    },
    actions : {
      display    : 'flex',
      alignItems : 'center',
      gap        : '16px'
    },
    exportButton : {
      display           : 'inline-flex',
      alignItems        : 'center',
      gap               : '8px',
      padding           : '4px',
      border            : 'none',
      borderRadius      : '4px',
      background        : 'none',
      cursor            : 'pointer',
      fontFamily        : 'Inter',
      fontWeight        : 500,
      fontSize          : '12px',
      lineHeight        : '16px',
      color             : dlsColor('grey1'),
      '&:focus-visible' : {
        outline       : `2px solid ${dlsColor('aaBlue')}`,
        outlineOffset : '2px'
      }
    }
  };
});

/**
 * Renders the current theme's Fault Codes modal, opened from the Diagnostics
 * card's View Details: a centred 880px modal - title and Close, an
 * `OBDII DTC View` toolbar with the active fault count tag and Export, and
 * the fault codes `DataTable`. Its height follows its rows up to
 * `min(890px, 100vh - 134px)`, after which only the table's body scrolls
 * under its sticky header.
 *
 * The modal and its scrim cover only the workspace (`workspaceId`), so the
 * left navigation stays visible and interactive. It closes on Close, Esc
 * and a scrim click; MUI's Dialog moves focus into it on open and returns
 * focus to the control that opened it on close.
 *
 * ```typescript
 *
 *   import {FaultCodesModal} from
 *     '@ion/dls/src/components/assetdetails/current/FaultCodesModal';
 *   ...
 *
 *   <FaultCodesModal open={isOpen}
 *     onClose={onClose}
 *     title='Fault Codes'
 *     viewTitle='OBDII DTC View'
 *     countLabel='18 Active Faults'
 *     exportLabel='Export'
 *     closeLabel='Close'
 *     onExport={onExport}
 *     columns={columns}
 *     rows={rows} />
 *
 * ```
 *
 * @param   open        Shows the modal when `true`.
 * @param   onClose     Invoked by Close, Esc or a scrim click.
 * @param   title       The modal's title.
 * @param   viewTitle   The table's title.
 * @param   countLabel  The active fault count tag's text.
 * @param   exportLabel The Export action's text.
 * @param   closeLabel  The Close button's accessible name.
 * @param   onExport    Invoked by Export.
 * @param   columns     The table's columns.
 * @param   rows        The table's rows.
 * @param   workspaceId The element the modal and scrim cover.
 * @returns             The fault codes modal.
 */
export const FaultCodesModal = ({
  open,
  onClose,
  title,
  viewTitle,
  countLabel,
  exportLabel,
  closeLabel,
  onExport,
  columns,
  rows,
  workspaceId = 'layout-root'
}: FaultCodesModalProps) => {
  const classes     = useStyles();
  const rect        = useWorkspaceRect(workspaceId, open);
  const dialogStyle = rect ? {
    top    : rect.top,
    left   : rect.left,
    width  : rect.width,
    height : rect.height,
    right  : 'auto',
    bottom : 'auto'
  } : undefined;

  return (
    <Dialog open={open}
      onClose={onClose}
      maxWidth={false}
      aria-labelledby={TITLE_ID}
      classes={{paper: classes.paper}}
      style={dialogStyle}
      BackdropProps={{style: {position: 'absolute'}}}>
      <div className={classes.header}>
        <h2 id={TITLE_ID} className={classes.title}>
          {title}
        </h2>
        <span className={classes.close}>
          <CloseButton aria-label={closeLabel} onClick={onClose} />
        </span>
      </div>
      <div className={classes.body}>
        <div className={classes.toolbar}>
          <h3 className={classes.viewTitle}>
            {viewTitle}
          </h3>
          <div className={classes.actions}>
            <SeverityTag label={countLabel} />
            <button type='button' className={classes.exportButton} onClick={onExport}>
              <SystemSaveExportIcon size={IconSize.medium} color={dlsColor('grey1')} />
              {exportLabel}
            </button>
          </div>
        </div>
        <DataTable columns={columns} rows={rows} />
      </div>
    </Dialog>
  );
};

export default memo(FaultCodesModal);
